import { useMemo } from 'react'
import type { CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { useInvoices } from '@klear/ui/hooks/use-invoices'
import { listItemEffectiveDate } from '@klear/order-event/list-display'
import toNumber from 'lodash/toNumber'
import toPairs from 'lodash/toPairs'

export type CustomerGrowthItem = {
  name: string
  growthRate: number
}

type MonthCustomerEntry = {
  monthKey: string
  customerId: number
  customerName: string
  value: Money | null
}

export function useCustomerGrowthData(traderId: number, selectedCurrency: CurrencyCode) {
  const { invoices, isLoading, error } = useInvoices({ traderId })

  const validEvents = useMemo(() => {
    return invoices.filter((inv) => {
      const money = inv.amount
      return money != null && money.currency === selectedCurrency && money.isPositive()
    })
  }, [invoices, selectedCurrency])

  const monthsData = useMemo(() => {
    if (!validEvents.length) return []

    const monthCustomerMap = new Map<string, MonthCustomerEntry>()

    validEvents.forEach((inv) => {
      const effectiveDate = listItemEffectiveDate(inv)
      if (!effectiveDate || !inv.amount) return

      const monthKey = effectiveDate.toString('YYYY-MM-01')
      const customerId = inv.customerId
      if (!customerId) return
      const customerName = inv.customerName || 'Unknown'
      const key = `${monthKey}-${customerId}`

      if (!monthCustomerMap.has(key)) {
        monthCustomerMap.set(key, { monthKey, customerId, customerName, value: null })
      }

      const entry = monthCustomerMap.get(key)!
      entry.value = entry.value ? entry.value.add(inv.amount) : inv.amount
    })

    const byMonth = new Map<string, Array<MonthCustomerEntry & { value: Money }>>()
    monthCustomerMap.forEach((entry) => {
      if (entry.value == null) return
      if (!byMonth.has(entry.monthKey)) byMonth.set(entry.monthKey, [])
      byMonth.get(entry.monthKey)!.push({ ...entry, value: entry.value })
    })

    return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, entries]) => entries)
  }, [validEvents])

  const { growingCustomers, decliningCustomers } = useMemo(() => {
    if (monthsData.length < 2) {
      return { growingCustomers: [], decliningCustomers: [] }
    }

    const recentMonths = monthsData.slice(-3)
    const priorMonths = monthsData.slice(-6, -3)

    if (!priorMonths.length) {
      return { growingCustomers: [], decliningCustomers: [] }
    }

    const customerVolume: Record<string, { recent: number; prior: number }> = {}

    recentMonths.forEach((month) => {
      month.forEach(({ customerName, value }) => {
        if (!customerVolume[customerName]) customerVolume[customerName] = { recent: 0, prior: 0 }
        const minor = toNumber(value.amount.toString())
        customerVolume[customerName]!.recent += minor
      })
    })

    priorMonths.forEach((month) => {
      month.forEach(({ customerName, value }) => {
        if (!customerVolume[customerName]) customerVolume[customerName] = { recent: 0, prior: 0 }
        const minor = toNumber(value.amount.toString())
        customerVolume[customerName]!.prior += minor
      })
    })

    const rates = toPairs(customerVolume)
      .filter(([, data]) => data.prior > 0 && data.recent > 0)
      .map(([name, data]) => ({
        name,
        growthRate: ((data.recent - data.prior) / data.prior) * 100
      }))

    const growingCustomers: CustomerGrowthItem[] = rates
      .filter((c) => c.growthRate > 0)
      .sort((a, b) => b.growthRate - a.growthRate)
      .slice(0, 5)

    const decliningCustomers: CustomerGrowthItem[] = rates
      .filter((c) => c.growthRate < 0)
      .sort((a, b) => a.growthRate - b.growthRate)
      .slice(0, 5)

    return { growingCustomers, decliningCustomers }
  }, [monthsData])

  return { growingCustomers, decliningCustomers, isLoading, error }
}
